import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TIMESHEET_STATUSES, formatValue } from "../data/data";
import {
  getProjectDetails,
  getProjectTasks,
  getTimesheets,
  createTimesheet,
  updateTimesheet,
  createTask,
  updateTask,
  updateProject,
  getTaskAttachments,
  uploadTaskAttachment,
  getTaskStateCounts,
  getUsersByIds,
  getSelectableUsers,
  callTaskAction
} from "../service/rpc";
import { Autocomplete, Checkbox, Chip, TextField } from "@mui/material";
import { useUser } from "../context/UserContext";
import toast from "react-hot-toast";
import SearchBar from "../components/SearchBar";
import Pagination from "../components/Pagination";
import Modal from "../components/Modal";
import "../styles/ContractDetail.css";

const userFieldStyles = {
  "& .MuiOutlinedInput-root": {
    minHeight: "42px",
    alignItems: "flex-start",
    padding: "4px 8px",
    borderRadius: "10px",
    fontFamily: "'DM Sans', sans-serif",
    backgroundColor: "#f8fafc",
    "& fieldset": {
      borderColor: "#e2e8f0",
      borderWidth: "1.5px",
    },
    "&:hover fieldset": {
      borderColor: "#cbd5e1",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#6b7fc4",
      boxShadow: "0 0 0 3px rgba(107, 127, 196, 0.12)",
    },
  },
  "& .MuiAutocomplete-input": {
    fontSize: "14px",
    color: "#1e293b",
    fontFamily: "'DM Sans', sans-serif",
  },
  "& .MuiAutocomplete-tag": { 
    margin: "4px 4px 0 0",
    height: "24px",
    borderRadius: "999px",
    backgroundColor: "#eef2ff",
    color: "#3b4b8f",
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 600,
    "& .MuiChip-deleteIcon": {
      color: "#6b7fc4"
    }
  },
};


const ITEMS_PER_PAGE = 6;

const EMPTY_TS_FORM = { task: "", date: "", hours: "" };
const EMPTY_TASK_FORM = { title: "", due: "", state: "todo", description: "", user_ids: [], responsible_user_id: "", allocated_hours: "" };

const TASK_STATES = {
  "todo": "To Do",
  "in_progress": "In Progress",
  "done": "Done",
  "cancelled": "Cancelled"
};

const handleAttachmentAction = async (attachmentId, action = 'view', fileName = 'attachment') => {
  try {
    const url = `/web/content/${attachmentId}${action === 'download' ? '?download=true' : ''}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to fetch attachment");

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    if (action === 'download') {
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      window.open(objectUrl, '_blank');
    }

    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
  } catch (err) {
    console.error("Action failed:", err);
    toast.error("Failed to load attachment. Please try again.");
  }
};


//



const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Timesheets");

  const [tsSearch, setTsSearch] = useState("");
  const [tsStatus, setTsStatus] = useState("All");
  const [tsPage, setTsPage] = useState(1);
  const [tsView, setTsView] = useState("list");

  const [taskSearch, setTaskSearch] = useState("");
  const [taskStatus, setTaskStatus] = useState("All");
  const [taskPage, setTaskPage] = useState(1);
  const [taskView, setTaskView] = useState("list");

  const [error, setError] = useState(null);
  const [contract, setContract] = useState(null);
  const [timesheetsData, setTimesheetsData] = useState([]);
  const [tasksData, setTasksData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { uid, loading: userLoading, name: userName, profileType } = useUser();

  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [headerForm, setHeaderForm] = useState({ name: "", budget: 0, startDate: "", endDate: "", contract_type: "", hourly_amount: "" });
  const [headerSaving, setHeaderSaving] = useState(false);

  const [isCreatingTs, setIsCreatingTs] = useState(false);
  const [editingTsId, setEditingTsId] = useState(null);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);

  const [tsForm, setTsForm] = useState(EMPTY_TS_FORM);
  const [taskForm, setTaskForm] = useState(EMPTY_TASK_FORM);

  const [tsSaving, setTsSaving] = useState(false);
  const [taskSaving, setTaskSaving] = useState(false);

  const [isTsModalOpen, setIsTsModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploadSuccessTaskId, setUploadSuccessTaskId] = useState(null);
  const [serverTaskStatuses, setServerTaskStatuses] = useState([]);
  const [users, setUsers] = useState([]);

  // Auto-save refs
  const tsLastSaved = useRef(null);
  const taskLastSaved = useRef(null);
  const headerLastSaved = useRef(null);



  // Modal State - View Attachments (Keeping this as a modal as it's purely for viewing)
  const [viewAttModalOpen, setViewAttModalOpen] = useState(false);
  const [viewAttTaskId, setViewAttTaskId] = useState(null);
  const [viewAttachments, setViewAttachments] = useState([]);
  const [viewAttachmentsLoading, setViewAttachmentsLoading] = useState(false);

  // Hold Modal
  const [holdModalOpen, setHoldModalOpen] = useState(false);
  const [holdTaskId, setHoldTaskId] = useState(null);
  const [holdForm, setHoldForm] = useState({ hold_type: "no_hold", hold_reason: "" });

  //mobile view show kanban default

  useEffect(() => {
    if (window.innerWidth < 768) {
      setTsView("timesheet");
      setTaskView("task");
    }
  }, []);

  // Fetch ALL selectable users on mount so the dropdown is never empty
  useEffect(() => {
    if (userLoading) return;
    const loadUsers = async () => {
      try {
        const allUsers = await getSelectableUsers({
          currentUserId: uid,
          currentUserName: userName,
        });
        if (allUsers?.length > 0) {
          setUsers(allUsers);
        }
      } catch (e) {
        console.warn('Failed to load selectable users:', e.message);
      }
    };
    loadUsers();
  }, [uid, userName, userLoading]);





  const refreshData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const tsDomain = [['project_id', '=', parseInt(id)]];
      const taskDomain = [['project_id', '=', parseInt(id)]];

      const [projectData, tasksResp, timesheetsResp, taskStateGroups] = await Promise.all([
        getProjectDetails(id),
        getProjectTasks(taskDomain),
        getTimesheets(tsDomain),
        getTaskStateCounts(taskDomain)
      ]);

      if (taskStateGroups && taskStateGroups.length > 0) {
        const stateLabels = taskStateGroups.map(g => TASK_STATES[g.state] || g.state);
        setServerTaskStatuses(stateLabels);
      } else {
        setServerTaskStatuses([]);
      }

      if (!projectData) {
        setContract(null);
        setError("Project not found or unable to load details.");
        return;
      }

      if (projectData) {
        setContract({
          id: projectData.id,
          name: projectData.name || "Unnamed Project",
          client: projectData.partner_id ? projectData.partner_id[1] : "Unknown",
          budget: projectData.budget || 0,
          status: "Active",
          startDate: projectData.date_start ? projectData.date_start.split(' ')[0] : "N/A",
          endDate: projectData.date_end ? projectData.date_end.split(' ')[0] : "N/A",
          contract_type: projectData.contract_type || "",
          hourly_amount: projectData.contract_type === 'hourly' ? projectData.hourly_amount :
            projectData.contract_type === 'fixed' ? projectData.fixed_amount :
              projectData.contract_type === 'monthly' ? projectData.monthly_amount :
                projectData.contract_type === 'weekly' ? projectData.weekly_amount : ""
        });

        const projectUserIds = Array.isArray(projectData.user_id)
          ? projectData.user_id.filter(v => typeof v === 'number')
          : [];
        // If projects have user IDs not yet in our users list, resolve & merge them
        if (projectUserIds.length > 0) {
          setUsers(prev => {
            const existingIds = new Set(prev.map(u => u.id));
            const missing = projectUserIds.filter(id => !existingIds.has(id));
            if (missing.length === 0) return prev;
            getUsersByIds(missing).then(resolved => {
              if (resolved?.length > 0) {
                setUsers(current => {
                  const seen = new Map(current.map(u => [u.id, u]));
                  resolved.forEach(u => { if (!seen.has(u.id)) seen.set(u.id, u); });
                  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
                });
              }
            }).catch(() => { });
            return prev;
          });
        }

      }

      const formatStatus = (val) => {
        return TASK_STATES[val] || "In Progress";
      };

      const formattedTasks = tasksResp.map(tk => ({
        id: tk.id,
        title: tk.name || "Unnamed Task",
        status: tk.state ? formatStatus(tk.state) : "In Progress",
        state_raw: tk.state || "todo",
        assignee: "Self",
        partner_id_raw: null,
        due: tk.date_deadline ? tk.date_deadline.split(' ')[0] : "N/A",
        description: stripHtml(tk.description),
        hold_type: tk.hold_type || "no_hold",
        hold_reason: tk.hold_reason || "",
        user_ids: Array.isArray(tk.user_id) ? tk.user_id.map(u => typeof u === 'object' ? u[0] : u) : [],
        responsible_user_id: tk.responsible_user_id ? tk.responsible_user_id[0] : "",
        responsible_user_name: tk.responsible_user_id ? tk.responsible_user_id[1] : "",
        allocated_hours: tk.allocated_hours || "",
        consumed_hours: (tk.consumed_hours || 0).toFixed(2),
        remaining_hours: (tk.remaining_hours || 0).toFixed(2)
      }));
      setTasksData(formattedTasks);

      const formattedTimesheets = timesheetsResp.timesheets.map(ts => ({
        id: ts.id,
        employee: ts.user_id ? ts.user_id[1] : "Unknown",
        date: ts.create_date ? ts.create_date.split(' ')[0] : "N/A",
        hours: (ts.duration || 0).toFixed(2),
        task: ts.name || (ts.task_id ? ts.task_id[1] : "General"),
        status: "Done"
      }));
      setTimesheetsData(formattedTimesheets);
    } catch (err) {
      console.error("Failed to refresh details", err);
      setError("Error refreshing project content: " + (err.message || "Please check your network and try again."));
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [id]);


  useEffect(() => {
    if (id && !userLoading && uid) {
      refreshData();
    }
  }, [id, uid, userLoading, refreshData]);

  const cancelTsEdit = () => {
    setIsTsModalOpen(false);
    setEditingTsId(null);
    setTsForm(EMPTY_TS_FORM);
  };

  const handleTsSave = async () => {
    const isCreating = editingTsId === null;
    const isDirty = JSON.stringify(tsForm) !== JSON.stringify(tsLastSaved.current);
    if (!isDirty && !isCreating) return;

    if (!uid) {
      toast.error("You must be logged in to log time.");
      return;
    }

    try {
      setTsSaving(true);
      const payload = {
        name: tsForm.task,
        project_id: parseInt(id),
        duration: parseFloat(tsForm.hours) || 0,
        user_id: uid,
        date_start: new Date().toISOString().replace('T', ' ').substring(0, 19)
      };

      if (isCreating) {
        const newId = await createTimesheet(payload);
        setTimesheetsData(prev => [{
          id: newId,
          employee: userName,
          date: tsForm.date || new Date().toISOString().split('T')[0],
          hours: parseFloat(tsForm.hours) || 0,
          task: tsForm.task,
          status: "Done"
        }, ...prev]);
      } else {
        await updateTimesheet(editingTsId, payload);
        setTimesheetsData(prev => prev.map(ts => ts.id === editingTsId ? {
          ...ts,
          date: tsForm.date,
          hours: parseFloat(tsForm.hours) || 0,
          task: tsForm.task
        } : ts));
      }

      tsLastSaved.current = { ...tsForm };
      toast.success(isCreating ? "Logged!" : "Saved!");
      cancelTsEdit();
    } catch (err) {
      console.error("TS Save failed", err);
      toast.error("Save failed.");
    } finally {
      setTsSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    return parts.length === 1
      ? parts[0][0].toUpperCase()
      : (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const cancelTaskEdit = () => {
    setIsTaskModalOpen(false);
    setEditingTaskId(null);
    setTaskForm(EMPTY_TASK_FORM);
    setPendingAttachments([]);
  };

  const handleTaskAction = async (taskId, actionName) => {
    // Intercept Hold Wizard to show our custom React Modal
    if (actionName === 'action_open_hold_wizard') {
      const task = tasksData.find(t => t.id === taskId);
      setHoldTaskId(taskId);
      setHoldForm({
        hold_type: task?.hold_type || "no_hold",
        hold_reason: task?.hold_reason || ""
      });
      setHoldModalOpen(true);
      return;
    }

    try {
      setTaskSaving(true);

      // action_reset has a server-side bug: its Python method creates a timesheet
      // record without setting the mandatory date_start field. Bypass it by
      // directly writing state = 'todo' on the task record instead.
      if (actionName === 'action_reset') {
        await updateTask(taskId, { state: 'todo' });
      } else {
        await callTaskAction(taskId, actionName);
      }

      toast.success("Action completed!");

      // Re-fetch data instead of full page reload
      await refreshData(false);

    } catch (err) {
      console.error(`Action ${actionName} failed`, err);
      toast.error("Action failed.");
    } finally {
      setTaskSaving(false);
    }
  };

  const handleHoldConfirm = async () => {
    try {
      setTaskSaving(true);
      // 1. Update the task fields
      await updateTask(holdTaskId, {
        hold_type: holdForm.hold_type,
        hold_reason: holdForm.hold_reason
      });

      // 2. Trigger the Odoo action to actually put it on hold
      // (Assuming the action logic handles the state change based on these fields)
      await callTaskAction(holdTaskId, 'action_open_hold_wizard');

      toast.success("Task put on Hold!");
      setHoldModalOpen(false);

      // Re-fetch data instead of full page reload
      await refreshData(false);
    } catch (err) {
      console.error("Hold failed", err);
      toast.error("Could not set Hold.");
    } finally {
      setTaskSaving(false);
    }
  };

  const handleTaskSave = async () => {
    const isCreating = editingTaskId === null;
    const isDirty = JSON.stringify(taskForm) !== JSON.stringify(taskLastSaved.current);
    if (!isDirty && !isCreating) return;

    try {
      setTaskSaving(true);
      const payload = {
        project_id: parseInt(id),
        name: taskForm.title,
        date_deadline: taskForm.due || false,
        state: taskForm.state || "todo",
        description: taskForm.description || "",
        // many2many: [[6, 0, [ids]]] replaces all
        user_id: taskForm.user_ids.length > 0 ? [[6, 0, taskForm.user_ids.map(Number)]] : [[6, 0, []]],
        responsible_user_id: taskForm.responsible_user_id ? parseInt(taskForm.responsible_user_id) : false,
        allocated_hours: parseFloat(taskForm.allocated_hours) || 0,
      };

      if (isCreating) {
        const newId = await createTask(payload);
        if (pendingAttachments.length > 0) {
          try {
            for (let att of pendingAttachments) {
              await uploadTaskAttachment(newId, att.name, att.base64);
            }
          } catch (e) { console.error("Error uploading pending attachments", e); }
        }
        setTasksData(prev => [{
          id: newId,
          title: taskForm.title,
          status: TASK_STATES[payload.state] || "In Progress",
          state_raw: payload.state,
          description: taskForm.description || "",
          assignee: userName,
          partner_id_raw: null,
          due: taskForm.due || "N/A",
          user_ids: taskForm.user_ids,
          responsible_user_id: taskForm.responsible_user_id ? parseInt(taskForm.responsible_user_id) : "",
          responsible_user_name: users.find(u => u.id === parseInt(taskForm.responsible_user_id))?.name || "",
          allocated_hours: taskForm.allocated_hours || ""
        }, ...prev]);
      } else {
        await updateTask(editingTaskId, payload);
        setTasksData(prev => prev.map(tk => tk.id === editingTaskId ? {
          ...tk,
          title: taskForm.title,
          status: TASK_STATES[payload.state] || "In Progress",
          state_raw: payload.state,
          description: taskForm.description || "",
          due: taskForm.due || "N/A",
          user_ids: taskForm.user_ids,
          responsible_user_id: taskForm.responsible_user_id ? parseInt(taskForm.responsible_user_id) : "",
          responsible_user_name: users.find(u => u.id === parseInt(taskForm.responsible_user_id))?.name || "",
          allocated_hours: taskForm.allocated_hours || ""
        } : tk));
      }

      taskLastSaved.current = { ...taskForm };
      toast.success(isCreating ? "Created!" : "Saved!");
      await refreshData(false);
      cancelTaskEdit();
    } catch (err) {
      console.error("Task Save failed", err);
      toast.error("Save failed.");
    } finally {
      setTaskSaving(false);
    }
  };



  const handleHeaderSave = async () => {
    const isDirty = JSON.stringify(headerForm) !== JSON.stringify(headerLastSaved.current);
    if (!isDirty) return;

    try {
      setHeaderSaving(true);
      const payload = {
        name: headerForm.name,
        budget: parseFloat(headerForm.budget) || 0,
        start_date: headerForm.startDate || false,
        date_deadline: headerForm.endDate || false,
        contract_type: headerForm.contract_type || false,
        hourly_amount: headerForm.contract_type === 'hourly' ? (parseFloat(headerForm.hourly_amount) || 0) : false,
        fixed_amount: headerForm.contract_type === 'fixed' ? (parseFloat(headerForm.hourly_amount) || 0) : false,
        monthly_amount: headerForm.contract_type === 'monthly' ? (parseFloat(headerForm.hourly_amount) || 0) : false,
        weekly_amount: headerForm.contract_type === 'weekly' ? (parseFloat(headerForm.hourly_amount) || 0) : false,
      };
      await updateProject(id, payload);
      setContract({
        ...contract,
        name: headerForm.name,
        budget: headerForm.budget,
        startDate: headerForm.startDate,
        endDate: headerForm.endDate,
        contract_type: headerForm.contract_type,
        hourly_amount: headerForm.hourly_amount
      });

      headerLastSaved.current = { ...headerForm };
      toast.success("Saved!");
      setIsEditingHeader(false);
    } catch (err) {
      console.error("Header Save failed", err);
      toast.error("Save failed.");
    } finally {
      setHeaderSaving(false);
    }
  };

  const filteredTimesheets = useMemo(() => {
    const raw = timesheetsData;
    const q = tsSearch.trim().toLowerCase();

    return raw.filter(ts => {
      const matchStatus = tsStatus === "All" || ts.status === tsStatus;
      const matchSearch = !q ||
        ts.employee.toLowerCase().includes(q) ||
        ts.task.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [id, tsSearch, tsStatus, timesheetsData]);

  useMemo(() => setTsPage(1), [tsSearch, tsStatus]);

  const pagedTimesheets = useMemo(() => {
    const start = (tsPage - 1) * ITEMS_PER_PAGE;
    return filteredTimesheets.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTimesheets, tsPage]);

  const filteredTasks = useMemo(() => {
    const raw = tasksData;
    const q = taskSearch.trim().toLowerCase();

    return raw.filter(tk => {
      const matchStatus = taskStatus === "All" || tk.status === taskStatus;
      const matchSearch = !q ||
        tk.title.toLowerCase().includes(q) ||
        tk.assignee.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [id, taskSearch, taskStatus, tasksData]);

  useMemo(() => setTaskPage(1), [taskSearch, taskStatus]);

  const pagedTasks = useMemo(() => {
    const start = (taskPage - 1) * ITEMS_PER_PAGE;
    return filteredTasks.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTasks, taskPage]);

  const dynamicTaskStatuses = useMemo(() => {
    // Prefer server-driven statuses if available, otherwise use client-side
    if (serverTaskStatuses.length > 0) {
      return ["All", ...serverTaskStatuses];
    }
    // Fallback: compute from client-side data
    if (tasksData.length === 0) return [];
    const statuses = new Set(tasksData.map(t => t.status));
    return ["All", ...Array.from(statuses)];
  }, [tasksData, serverTaskStatuses]);

  const getBadgeClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("done") || s.includes("approved") || s.includes("closed") || s === "done") return "active";
    if (s.includes("progress") || s.includes("pending") || s.includes("doing") || s.includes("in progress")) return "pending";
    if (s.includes("hold") || s.includes("wait") || s.includes("pause")) return "hold";
    if (s.includes("to do") || s.includes("new") || s.includes("open") || s === "todo") return "todo";
    return "cancelled";
  };

  if (loading) {
    return (
      <main className="main">
        <div className="loader-container">
          <div className="loader"></div>
          <p style={{ color: '#64748b', fontWeight: '500', fontFamily: 'Sora' }}>Fetching project details...</p>
        </div>
      </main>
    );
  }

  if (error) return (
    <main className="main">
      <div className="error-container" style={{ textAlign: 'center', padding: '100px 20px' }}>
        <div style={{ color: '#ef4444', fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h2 style={{ color: '#1e293b', marginBottom: '10px', fontFamily: 'Sora' }}>Access Restricted</h2>
        <p style={{ color: '#64748b', marginBottom: '20px' }}>{error}</p>
        <button className="cd-back" style={{ margin: '0 auto' }} onClick={() => navigate("/contracts")}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Contracts
        </button>
      </div>
    </main>
  );

  if (!contract) {
    return (
      <main className="main">
        <div className="cd-header">
          <button className="cd-back" onClick={() => navigate("/contracts")}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
              viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back to Contracts
          </button>
          <h1 className="cd-name">Contract not found</h1>
        </div>
      </main>
    );
  }

  return (
    <main className="main">
      <style>{`
        .MuiAutocomplete-popper {
          z-index: 10000 !important;
        }
      `}</style>
      
      {/* ── MODALS ── */}

      {/* View Attachments Modal */}
      <Modal isOpen={viewAttModalOpen} onClose={() => setViewAttModalOpen(false)} title="Task Attachments">
        <div className="modal-body" style={{ padding: '' }}>
          {viewAttachmentsLoading ? (
            <p style={{ fontSize: '14px', color: '#64748b', textAlign: 'center', padding: '20px' }}>Loading attachments...</p>
          ) : viewAttachments.length === 0 ? (
            <p style={{ fontSize: '14px', color: '#64748b', textAlign: 'center', padding: '20px' }}>No attachments found for this task.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {viewAttachments.map(att => (
                <div className="task-view" key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', padding: '12px 16px', borderRadius: '10px', border: '1px solid #eef2f6' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7fc4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>
                    <button
                      onClick={() => handleAttachmentAction(att.id, 'view')}
                      style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: '14px', color: '#1e293b', fontWeight: '500', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}
                      title="View File"
                      onMouseOver={e => e.target.style.textDecoration = 'underline'}
                      onMouseOut={e => e.target.style.textDecoration = 'none'}
                    >
                      {att.name}
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1px', flexShrink: 0 }}>
                    <button onClick={() => handleAttachmentAction(att.id, 'download', att.name)} style={{ cursor: 'pointer', border: 'none', color: '#16a34a', display: 'flex', alignItems: 'center', background: '#dcfce7', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', textDecoration: 'none', transition: 'all 0.2s' }} title="Download File" onMouseOver={e => e.currentTarget.style.background = '#bbf7d0'} onMouseOut={e => e.currentTarget.style.background = '#dcfce7'}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '2px' }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>

                    </button>
                    <span style={{ fontSize: '12px', color: '#94a3b8', minWidth: '48px', textAlign: 'right' }}>{(att.file_size / 1024).toFixed(1)} KB</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Log/Edit Timesheet Modal */}
      <Modal
        isOpen={isTsModalOpen}
        onClose={cancelTsEdit}
        title={editingTsId ? "Edit Timesheet" : "Log Time"}
        onSave={handleTsSave}
        saving={tsSaving}
      >
        <div className="modal-form">
          <div className="form-group">
            <label className="form-label">Date</label>
            <input
              className="form-input"
              type="date"
              value={tsForm.date}
              onChange={e => setTsForm({ ...tsForm, date: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Duration (Hours)</label>
            <input
              className="form-input"
              type="number"
              step="0.5"
              value={tsForm.hours}
              onChange={e => setTsForm({ ...tsForm, hours: e.target.value })}
              placeholder="0.0"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              style={{ minHeight: '100px' }}
              value={tsForm.task}
              onChange={e => setTsForm({ ...tsForm, task: e.target.value })}
              placeholder="What did you work on?"
              rows={3}
            />
          </div>
        </div>
      </Modal>

      {/* Create/Edit Task Modal */}
      <Modal
        isOpen={isTaskModalOpen}
        onClose={cancelTaskEdit}
        title={editingTaskId ? "Edit Task" : "New Task"}
        onSave={handleTaskSave}
        saving={taskSaving}
      >
        <div className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Task Title</label>
              <input
                className="form-input"
                value={taskForm.title}
                onChange={e => setTaskForm({ ...taskForm, title: e.target.value })}
                placeholder="Task name..."
              />
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input
                className="form-input"
                type="date"
                value={taskForm.due}
                onChange={e => setTaskForm({ ...taskForm, due: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-input"
                value={taskForm.state}
                onChange={e => setTaskForm({ ...taskForm, state: e.target.value })}
              >
                {Object.entries(TASK_STATES).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            {profileType === 'consultant' && (
              <div className="form-group">
                <label className="form-label">Allocated Hours</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.5"
                  value={taskForm.allocated_hours}
                  onChange={e => setTaskForm({ ...taskForm, allocated_hours: e.target.value })}
                  placeholder="0.0"
                />
              </div>
            )}
          </div>
          {profileType === 'consultant' && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Responsible User</label>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => option?.name || ""}
                  value={users.find(u => String(u.id) === String(taskForm.responsible_user_id)) || null}
                  onChange={(_, newValue) => {
                    setTaskForm({ ...taskForm, responsible_user_id: newValue ? String(newValue.id) : "" });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select responsible..." size="small" />
                  )}
                  sx={userFieldStyles}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Assigned Users</label>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  disabled
                  options={users}
                  value={users.filter(u => taskForm.user_ids.includes(u.id))}
                  onChange={(_, newValue) => {
                    setTaskForm({ ...taskForm, user_ids: newValue.map(u => u.id) });
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option?.name || ""}
                  noOptionsText="No users found"
                  renderOption={(props, option, { selected }) => {
                    const { key, ...rest } = props;
                    return (
                      <li key={key} {...rest}>
                        <Checkbox checked={selected} size="small"
                          sx={{ mr: 1, color: "#94a3b8", "&.Mui-checked": { color: "#6b7fc4" } }} />
                        {option.name}
                      </li>
                    );
                  }}
                  renderInput={(params) => {
                    const { renderTags, ...restParams } = params;
                    return <TextField {...restParams} placeholder={taskForm.user_ids.length === 0 ? "Select users" : ""} size="small"/>;
                  }}
                  sx={userFieldStyles}
                />
              </div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={taskForm.description || ""}
              onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
              placeholder="Task details..."
              rows={3}
              style={{ minHeight: '80px' }}
            />
          </div>
        </div>
      </Modal>

      {/* ── DETAIL HEADER ── */}
      <div className="cd-header">
        <button className="cd-back" onClick={() => navigate("/contracts")}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Contracts
        </button>

        <div className="cd-meta">
          <div className="cd-identity">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h1 className="cd-name">{contract.name}</h1>
            </div>
          </div>

          <div className="cd-stats">
            <div className="cd-stat">
              <span className="cd-stat__label">Budget Value</span>
              <span className="cd-stat__value">{formatValue(contract.budget)}</span>
            </div>
            <div className="cd-divider" />
            <div className="cd-stat">
              <span className="cd-stat__label">Start</span>
              <span className="cd-stat__value">{contract.startDate}</span>
            </div>
            <div className="cd-divider" />
            <div className="cd-stat">
              <span className="cd-stat__label">End</span>
              <span className="cd-stat__value">{contract.endDate}</span>
            </div>
            {profileType === 'consultant' && (
              <>
                <div className="cd-divider" />

                <div className="cd-stat">
                  <span className="cd-stat__label">Contract Type</span>
                  <span
                    className="cd-stat__value"
                    style={{ textTransform: 'capitalize' }}
                  >
                    {contract.contract_type || "N/A"}
                  </span>
                </div>

                <div className="cd-divider" />
                <div className="cd-stat">
                  <span className="cd-stat__label">Amount</span>
                  <span className="cd-stat__value">
                    {contract.hourly_amount
                      ? `₹${contract.hourly_amount}`
                      : "N/A"}
                  </span>
                </div>
              </>
            )}
            <div className="cd-divider" />
          </div>
        </div>
      </div>

      {/* ── DETAIL TABS ── */}
      <div className="cd-tabs-wrap">
        <div className="cd-tabs">
          {["Timesheets", "Tasks"].map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`cd-tab ${activeTab === t ? "cd-tab--active" : ""}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* ── TIMESHEETS ── */}
      {activeTab === "Timesheets" && (
        <>

          <div className="task-tab-header">
            <button className="btn-create" style={{ padding: '8px 16px', fontSize: '13px' }} onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              const initial = { ...EMPTY_TS_FORM, date: today };
              setTsForm(initial);
              tsLastSaved.current = initial;
              setIsTsModalOpen(true);
              setEditingTsId(null);
            }}>
              + Log Time
            </button>
            <SearchBar
              search={tsSearch}
              setSearch={setTsSearch}
              placeholder="Search by employee or task…"
              style={{ flex: 1, margin: 0 }}
            />

            <div className="view-toggle">
              <button
                className={`view-toggle__btn ${tsView === 'list' ? 'view-toggle__btn--active' : ''}`}
                onClick={() => setTsView('list')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
                List
              </button>
              <button
                className={`view-toggle__btn ${tsView === 'grid' ? 'view-toggle__btn--active' : ''}`}
                onClick={() => setTsView('grid')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                </svg>
                Kanban
              </button>
            </div>
          </div>

          <div className="table-card">
            {tsView === "list" ? (
              <table className="table">
                <thead>
                  <tr>
                    <th className="table__th" style={{ width: '180px' }}>Date</th>
                    <th className="table__th" style={{ width: '120px' }}>Hours</th>
                    <th className="table__th">Description</th>
                    <th className="table__th" style={{ width: '80px', textAlign: 'right' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTimesheets.length === 0 ? (
                    <tr><td className="table__empty" colSpan={4}>No timesheets match your filter.</td></tr>
                  ) : filteredTimesheets.map((ts) => (
                    <tr key={ts.id} className="table__row">
                      <td className="table__td"><span className="table__client">{ts.date}</span></td>
                      <td className="table__td"><span className="table__value">{Number(ts.hours).toFixed(2)}h</span></td>
                      <td className="table__td"><span className="table__client">{ts.task}</span></td>
                      <td className="table__td" style={{ textAlign: 'right' }}>
                        <button className="btn-row-edit" onClick={() => {
                          setEditingTsId(ts.id);
                          const initial = { task: ts.task, date: ts.date, hours: String(ts.hours) };
                          setTsForm(initial);
                          tsLastSaved.current = initial;
                          setIsTsModalOpen(true);
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="task-grid">
                {pagedTimesheets.length === 0 ? (
                  <div className="table__empty" style={{ padding: '40px' }}>No timesheets match your filter.</div>
                ) : pagedTimesheets.map((ts) => (
                  <div key={ts.id} className="kanban-card">
                    <div style={{ display: 'flex', justifyContent: 'right ', alignItems: 'center', gap: '8px' }} className="kanban-card__top">
                      <button className="btn-row-edit" style={{ width: '26px', height: '26px' }} onClick={() => {
                        setEditingTsId(ts.id);
                        const initial = { task: ts.task, date: ts.date, hours: String(ts.hours) };
                        setTsForm(initial);
                        tsLastSaved.current = initial;
                        setIsTsModalOpen(true);
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </div>
                    <div style={{ fontSize: '14px', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: '1.5', marginBottom: '12px' }}>{ts.task}</div>
                    <div className="kanban-card__bottom">
                      <div style={{ fontWeight: '700', color: '#1a2332' }}>{ts.date}</div>
                      <span className="kanban-card__id">{Number(ts.hours).toFixed(2)}h</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Pagination
              currentPage={tsPage}
              totalItems={filteredTimesheets.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setTsPage}
            />
          </div>
        </>
      )}




      {/* ── TASKS ── */}
      {activeTab === "Tasks" && (
        <>
          <div className="task-tab-header">
            <button className="btn-create" style={{ padding: '8px 16px', fontSize: '13px' }} onClick={() => {
              const date = new Date();
              date.setDate(date.getDate() + 5);
              const defaultDue = date.toISOString().split('T')[0];
              const projectAssignedUserIds = users.map(u => u.id);
              const initial = {
                ...EMPTY_TASK_FORM,
                due: defaultDue,
                user_ids: projectAssignedUserIds,
                responsible_user_id: projectAssignedUserIds.length === 1 ? String(projectAssignedUserIds[0]) : ""
              };
              setTaskForm(initial);
              taskLastSaved.current = initial;
              setIsTaskModalOpen(true);
              setEditingTaskId(null);
              setPendingAttachments([]);
            }}>
              + New Task
            </button>

            <SearchBar
              search={taskSearch}
              setSearch={setTaskSearch}
              placeholder="Search by title or assignee…"
              style={{ flex: 1, margin: 0 }}
            />


            <div className="view-toggle">
              <button
                className={`view-toggle__btn ${taskView === 'list' ? 'view-toggle__btn--active' : ''}`}
                onClick={() => setTaskView('list')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
                List
              </button>
              <button
                className={`view-toggle__btn ${taskView === 'grid' ? 'view-toggle__btn--active' : ''}`}
                onClick={() => setTaskView('grid')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                </svg>
                Kanban
              </button>
            </div>
          </div>

          <div className="table-card">
            <div className="status-pills">
              {dynamicTaskStatuses.map((s) => (
                <button
                  key={s}
                  onClick={() => setTaskStatus(s)}
                  className={`status-pills__pill status-pills__pill--${getBadgeClass(s)} ${taskStatus === s ? "status-pills__pill--selected" : ""}`}
                >
                  {s}
                </button>
              ))}
            </div>

            {taskView === "list" ? (
              <table className="table">
                <thead>
                  <tr>
                    <th className="table__th">Date</th>
                    <th className="table__th">Task</th>
                    <th className="table__th">Status</th>
                    <th className="table__th" style={{ textAlign: "center" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedTasks.length === 0 ? (
                    <tr><td className="table__empty" colSpan={4}>No tasks match your filter.</td></tr>
                  ) : pagedTasks.map((tk) => (
                    <tr key={tk.id} className="table__row">
                      <td className="table__td"><span className="table__client">{tk.due}</span></td>
                      <td className="table__td">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span className="table__name">{tk.title}</span>
                          {tk.description && <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'pre-wrap' }}>{tk.description}</span>}
                          {profileType === 'consultant' && tk.hold_type && tk.hold_type !== 'no_hold' && (
                            <div style={{ marginTop: '4px', padding: '6px 10px', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '6px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: '700', color: '#dc2626', textTransform: 'uppercase' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                {tk.hold_type === 'client_hold' ? 'Hold by Client' : 'Hold by Us'}
                              </div>
                              {tk.hold_reason && <div style={{ fontSize: '12px', color: '#991b1b', marginTop: '2px', fontStyle: 'italic' }}>"{tk.hold_reason}"</div>}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="table__td">
                        <span className={`badge badge--${getBadgeClass(tk.status)}`}>{tk.status}</span>
                      </td>
                      <td className="table__td" style={{ textAlign: 'right', paddingRight: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                          {profileType === 'consultant' && (
                            <>
                              {tk.state_raw === 'todo' && (
                                <button className="btn-task-action btn-task-action--start" onClick={() => handleTaskAction(tk.id, 'action_start')} title="Start">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg> Start
                                </button>
                              )}
                              {tk.state_raw === 'in_progress' && (
                                <>
                                  <button className="btn-task-action btn-task-action--pause" onClick={() => handleTaskAction(tk.id, 'action_pause')} title="Pause">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg> Pause
                                  </button>
                                  <button className="btn-task-action btn-task-action--stop" onClick={() => handleTaskAction(tk.id, 'action_stop')} title="Stop">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" /></svg> Stop
                                  </button>
                                </>
                              )}
                              {tk.state_raw !== 'done' && (
                                <button className="btn-task-action btn-task-action--hold" onClick={() => handleTaskAction(tk.id, 'action_open_hold_wizard')} title="Hold">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg> Hold
                                </button>
                              )}
                              {tk.state_raw === 'done' && (
                                <button className="btn-task-action btn-task-action--reset" onClick={() => handleTaskAction(tk.id, 'action_reset')} title="Reset">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><polyline points="3 3 3 8 8 8" /></svg> Reset
                                </button>
                              )}
                            </>
                          )}
                          <div style={{ width: '1px', height: '16px', background: '#e2e8f0', margin: '0 4px' }} />
                          <button className="btn-row-view-att" style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', border: '1.5px solid #e8edf3', borderRadius: '6px', cursor: 'pointer', color: '#64748b', transition: 'all 0.2s' }} title="View Attachments" onClick={() => {
                            setViewAttTaskId(tk.id);
                            setViewAttModalOpen(true);
                            setViewAttachmentsLoading(true);
                            getTaskAttachments(tk.id).then(atts => { setViewAttachments(atts); setViewAttachmentsLoading(false); }).catch(() => setViewAttachmentsLoading(false));
                          }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                          </button>
                          <label className="btn-row-upload" style={{ cursor: 'pointer', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: uploadSuccessTaskId === tk.id ? '#e8f5f0' : '#f8fafc', border: `1.5px solid ${uploadSuccessTaskId === tk.id ? '#10b981' : '#e8edf3'}`, borderRadius: '6px', color: uploadSuccessTaskId === tk.id ? '#10b981' : '#6b7fc4', transition: 'all 0.3s' }} title="Attach File">
                            {uploadSuccessTaskId === tk.id ? (
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                            )}
                            <input type="file" style={{ display: 'none' }} onChange={async (e) => {
                              const file = e.target.files[0]; if (!file) return;
                              try {
                                const reader = new FileReader(); reader.onload = async (event) => {
                                  const base64 = event.target.result.split(',')[1];
                                  await uploadTaskAttachment(tk.id, file.name, base64); setUploadSuccessTaskId(tk.id); setTimeout(() => setUploadSuccessTaskId(null), 3000);
                                }; reader.readAsDataURL(file);
                              } catch (err) { console.error("Upload failed", err); }
                            }} />
                          </label>
                          <button className="btn-row-edit" style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', border: '1.5px solid #e8edf3', borderRadius: '6px', cursor: 'pointer', color: '#64748b', transition: 'all 0.2s' }} onClick={() => {
                            setEditingTaskId(tk.id);
                            const initial = { title: tk.title, due: tk.due === "N/A" ? "" : tk.due, state: tk.state_raw, description: tk.description || "", user_ids: tk.user_ids || [], responsible_user_id: tk.responsible_user_id ? String(tk.responsible_user_id) : "", allocated_hours: tk.allocated_hours ? String(tk.allocated_hours) : "" };
                            setTaskForm(initial);
                            taskLastSaved.current = initial;
                            setIsTaskModalOpen(true);
                          }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="task-grid">
                {pagedTasks.length === 0 ? (
                  <div className="table__empty" style={{ padding: '40px' }}>No tasks match your filter.</div>
                ) : pagedTasks.map((tk) => (
                  <div key={tk.id} className="kanban-card">
                    <div className="kanban-card__top">
                      <span className={`badge badge--${getBadgeClass(tk.status)}`}>{tk.status}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          className="btn-row-view-att"
                          style={{ width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', border: '1.5px solid #e8edf3', borderRadius: '6px', cursor: 'pointer', color: '#64748b', transition: 'all 0.2s' }}
                          title="View Attachments"
                          onClick={() => {
                            setViewAttTaskId(tk.id);
                            setViewAttModalOpen(true);
                            setViewAttachmentsLoading(true);
                            getTaskAttachments(tk.id).then(atts => {
                              setViewAttachments(atts);
                              setViewAttachmentsLoading(false);
                            }).catch(() => setViewAttachmentsLoading(false));
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </button>
                        <button
                          className="btn-row-edit"
                          style={{ width: '26px', height: '26px' }}
                          onClick={() => {
                            setEditingTaskId(tk.id);
                            const initial = {
                              title: tk.title,
                              due: tk.due === "N/A" ? "" : tk.due,
                              state: tk.state_raw,
                              description: tk.description || "",
                              user_ids: tk.user_ids || [],
                              responsible_user_id: tk.responsible_user_id ? String(tk.responsible_user_id) : "",
                              allocated_hours: tk.allocated_hours ? String(tk.allocated_hours) : ""
                            };
                            setTaskForm(initial);
                            taskLastSaved.current = initial;
                            setIsTaskModalOpen(true);
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <h3 className="kanban-card__title">{tk.title}</h3>
                    {tk.description && (
                      <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                        {tk.description}
                      </div>
                    )}

                    {profileType === 'consultant' && (
                      <div className="kanban-card__metrics_grid">
                        <div className="kanban-metrics__item">
                          <span className="kanban-metrics__label">Consumed</span>
                          <span className="kanban-metrics__value">{tk.consumed_hours}h</span>
                        </div>
                        <div className="kanban-metrics__item">
                          <span className="kanban-metrics__label">Remaining</span>
                          <span className="kanban-metrics__value" style={{ color: tk.remaining_hours < 0 ? '#ef4444' : 'inherit' }}>{tk.remaining_hours}h</span>
                        </div>
                        <div className="kanban-metrics__item">
                          <span className="kanban-metrics__label">Allocated</span>
                          <span className="kanban-metrics__value" style={{ color: tk.allocated_hours < 0 ? '#ef4444' : 'inherit' }}>{tk.allocated_hours}h</span>
                        </div>
                      </div>
                    )}

                    <div className="kanban-card__bottom" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div className="kanban-card__due">{tk.due}</div>
                      {profileType === 'consultant' && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                          {tk.state_raw === 'todo' && (
                            <button className="btn-task-action btn-task-action--start" onClick={() => handleTaskAction(tk.id, 'action_start')} style={{ padding: '4px 8px', fontSize: '11px' }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                              Start
                            </button>
                          )}
                          {tk.state_raw === 'in_progress' && (
                            <>
                              <button className="btn-task-action btn-task-action--pause" onClick={() => handleTaskAction(tk.id, 'action_pause')} style={{ padding: '4px 8px', fontSize: '11px' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
                                Pause
                              </button>
                              <button className="btn-task-action btn-task-action--stop" onClick={() => handleTaskAction(tk.id, 'action_stop')} style={{ padding: '4px 8px', fontSize: '11px' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" /></svg>
                                Stop
                              </button>
                            </>
                          )}
                          {tk.state_raw !== 'done' && (
                            <button className="btn-task-action btn-task-action--hold" onClick={() => handleTaskAction(tk.id, 'action_open_hold_wizard')} style={{ padding: '4px 8px', fontSize: '11px' }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                              Hold
                            </button>
                          )}
                          {tk.state_raw === 'done' && (
                            <button className="btn-task-action btn-task-action--reset" onClick={() => handleTaskAction(tk.id, 'action_reset')} style={{ padding: '4px 8px', fontSize: '11px' }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><polyline points="3 3 3 8 8 8" /></svg>
                              Reset
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Pagination
              currentPage={taskPage}
              totalItems={filteredTasks.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setTaskPage}
            />
          </div>
        </>
      )}

      {/* HOLD MODAL */}
      {holdModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Put Task on Hold</h3>
              <button className="modal-close" onClick={() => setHoldModalOpen(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div className="inline-field">
                  <label style={{ color: '#64748b', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>Hold Type</label>
                  <select
                    className="inline-input"
                    style={{ height: '45px', background: '#f8fafc' }}
                    value={holdForm.hold_type}
                    onChange={e => setHoldForm({ ...holdForm, hold_type: e.target.value })}
                  >
                    <option value="no_hold">No Hold</option>
                    <option value="client_hold">Hold by Client</option>
                    <option value="us_hold">Hold by Us</option>
                  </select>
                </div>
                <div className="inline-field">
                  <label style={{ color: '#64748b', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>Hold Reason</label>
                  <textarea
                    className="inline-input"
                    rows={4}
                    placeholder="Provide a reason for putting this task on hold..."
                    style={{ background: '#f8fafc', padding: '12px', fontSize: '14px' }}
                    value={holdForm.hold_reason}
                    onChange={e => setHoldForm({ ...holdForm, hold_reason: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-modal-cancel" onClick={() => setHoldModalOpen(false)}>Cancel</button>
              <button
                className="btn-modal-confirm"
                onClick={handleHoldConfirm}
                disabled={taskSaving}
              >
                {taskSaving ? "Processing..." : "Confirm Hold"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
